import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const GATEWAY_TABS = [
  { id: 'choose', label: 'Start here' },
  { id: 'field-zip', label: 'Gateway software' },
  { id: 'usb-iso', label: 'Installer USB stick' },
  { id: 'golden-image', label: 'Factory disk image' },
  { id: 'eyesense', label: 'Home server' },
];

const HUB_TABS = [
  { id: 'choose', label: 'Which path' },
  { id: 'images', label: 'ECS images' },
  { id: 'infra', label: 'Infrastructure' },
];


const ZIP_URL =
  'https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-ai-distro.zip';
const ISO_URL =
  'https://eyesense-downloads-435236352827.s3.us-east-1.amazonaws.com/eyesense-usb-installer.iso';

function CommandBlock({ command }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(command);
      ok = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = command;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.select();
      ok = document.execCommand('copy');
      document.body.removeChild(area);
    }
    setCopied(ok);
    if (ok) {
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        mb: 2,
        bgcolor: '#0f172a',
        color: '#e2e8f0',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
        <Button
          size="small"
          startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
          onClick={copy}
          sx={{ color: '#cbd5e1', textTransform: 'none' }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          px: 2,
          pb: 2,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {command}
      </Box>
    </Paper>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Steps({ items }) {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.25 } }}>
      {items.map((item) => (
        <li key={item}>
          <Typography variant="body1" component="span">
            {item}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

const GATEWAY_CHANNELS = [
  {
    change: 'Install or update the gateway services on Linux, macOS, or Windows with WSL2',
    run: 'Gateway software tab',
    receives: 'The distro zip: Docker Compose, setup scripts, gateway services, and the nightly updater. It does not install an operating system.',
  },
  {
    change: 'Turn a blank x86_64 computer into a complete EyeSense gateway',
    run: 'Installer USB stick tab, after publishing the gateway software',
    receives: 'One unattended installer: Ubuntu, Docker, the gateway services, first-boot wizard, and nightly updater.',
  },
  {
    change: 'Freeze the first factory batch as clones of one tested OEM disk',
    run: 'Factory disk image tab, after publishing the gateway software',
    receives: 'The first units receive a Clonezilla/dd copy of the sealed Kubuntu OEM disk. Later units use the USB installer.',
  },
  {
    change: 'The live gateway on the home eyesense server',
    run: 'Home server tab',
    receives: '~/deerdeterrent on that server. Field units and the ISO stay on the S3 channel.',
  },
];

const HUB_CHANNELS = [
  {
    change: 'Hub UI, hub Java, or a Dockerfile',
    run: 'ECS images tab',
    receives: 'New container images on ECS. Gateway software on a NUC or the home server stays put.',
  },
  {
    change: 'Load balancer rules, task size, security groups, RDS, or the WireGuard EC2',
    run: 'Infrastructure tab',
    receives: 'AWS resources from Terraform. Run ECS images afterward only if the images also changed.',
  },
];

function ChannelChooser({ channels }) {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('sm'));

  if (stacked) {
    return (
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {channels.map((row) => (
          <Paper key={row.change} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.change}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Run: </strong>{row.run}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{row.receives}</Typography>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Table size="small" sx={{ mb: 3 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>You are changing</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Run this</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>What receives it</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {channels.map((row) => (
          <TableRow key={row.change}>
            <TableCell>{row.change}</TableCell>
            <TableCell>{row.run}</TableCell>
            <TableCell>{row.receives}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function GatewayChooseTab() {
  return (
    <>
      <Section title="What the gateway is for">
        <Typography variant="body1" sx={{ mb: 1 }}>
          The gateway is the site-local EyeSense runtime. It runs the camera pipeline, local
          gateway UI, storage, database, and supporting services on hardware near the cameras.
          It connects to eyesense.ai through its own registered WireGuard connection.
        </Typography>
        <Typography variant="body1">
          Deploying a gateway at a location gives that site its own camera-processing and service
          stack while the hub provides remote registration, management, and CONNECT access. The
          choices below are different ways to prepare or update that same gateway stack.
        </Typography>
      </Section>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Use the table below to choose how you are preparing or updating that gateway. These are
        separate delivery methods, so updating one does not automatically rebuild the others.
      </Typography>
      <ChannelChooser channels={GATEWAY_CHANNELS} />
      <Alert severity="info" sx={{ mb: 2 }}>
        For a full release, publish the gateway software first. Then rebuild the USB installer or
        factory disk image when newly prepared computers should start with that release.
        The live home server is a separate git pull.
      </Alert>
      <Section title="Rules for every gateway package">
        <Steps
          items={[
            'Publish the gateway software zip with deploypkg.sh and the ISO with publish-iso.sh. Those scripts upload the file and the SHA sidecar and fail if the public hash does not match.',
            'Leave .env files, WireGuard keys, and DT- claim tokens out of the zip, the ISO, and the golden image.',
          ]}
        />
      </Section>
      <Typography variant="body2" color="text.secondary">
        Customer-facing install steps stay on{' '}
        <Link href="https://eyesense.ai/install-gateway" target="_blank" rel="noopener noreferrer">
          eyesense.ai/install-gateway
        </Link>
        . This page is the operator runbook.
      </Typography>
    </>
  );
}

function HubChooseTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        These options ship the hub at eyesense.ai. Gateway software on a NUC, a USB stick, or the
        home server stays on the Home gateway switch.
      </Typography>
      <ChannelChooser channels={HUB_CHANNELS} />
      <Section title="Rules for every hub change">
        <Steps
          items={[
            'Change AWS resources (load balancer rules, task size, security groups, the WireGuard EC2) with Terraform. The deploy script only builds images and rolls ECS tasks.',
            'Pick one image scope. --frontend-only, --gateway-only, and --management-only each roll one service. The command with no flag rolls all three.',
          ]}
        />
      </Section>
    </>
  );
}

function FieldZipTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        This package installs EyeSense software without installing an operating system. Use it for
        a first-time setup on Linux, macOS, or Windows with WSL2, or to update an existing gateway.
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        New installations run <code>setup.sh</code>. Installed appliances do not git pull; a daily
        timer reads a small SHA sidecar and downloads the ~2.7 GB zip only when that hash changes.
      </Typography>
      <Section title="1. Build the zip on your laptop">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Work in the <code>deeralarm</code> checkout (GitHub <code>tguless/deerdeterrent</code>).
          Run <code>--build-only</code> when the gateway JAR or gateway frontend changed.
        </Typography>
        <CommandBlock
          command={`cd deeralarm
./prepare-for-docker.sh --build-only
./package.sh gateway`}
        />
        <Typography variant="body2">
          <code>package.sh</code> wipes and recreates <code>distro/</code>, then writes{' '}
          <code>eyesense-ai-distro.zip</code> and <code>eyesense-ai-distro.zip.sha256</code>.
          Edit compose and scripts in the repo root. Edits inside <code>distro/</code> are discarded
          on the next build.
        </Typography>
      </Section>
      <Section title="2. Publish zip and sidecar">
        <CommandBlock command={`cd deeralarm
./deploypkg.sh`} />
        <Typography variant="body2">
          Default mode uploads the zip and the sidecar to S3 and checks that a public GET of the
          sidecar matches the local file. <code>--legacy</code> does that and also copies both files
          to the eyesense server. Skip <code>--no-sidecar</code> for any field or golden-image publish.
          Without a matching sidecar, every appliance can download the full zip every day.
        </Typography>
      </Section>
      <Section title="3. Verify the public files">
        <CommandBlock
          command={`curl -sI ${ZIP_URL} | head -5
curl -fsSL ${ZIP_URL}.sha256
shasum -a 256 deeralarm/eyesense-ai-distro.zip`}
        />
        <Typography variant="body2">
          The sidecar text must match the local hash (<code>sha256sum</code> on Linux). The public URL is{' '}
          <Link href={ZIP_URL} target="_blank" rel="noopener noreferrer">
            {ZIP_URL}
          </Link>
          .
        </Typography>
      </Section>
      <Section title="What a customer runs after this publish">
        <Typography variant="body2" sx={{ mb: 1 }}>
          New installs follow{' '}
          <Link href="https://eyesense.ai/install-gateway" target="_blank" rel="noopener noreferrer">
            the install guide
          </Link>
          . The commands are:
        </Typography>
        <CommandBlock
          command={`curl -L -o eyesense-ai-distro.zip \\
  ${ZIP_URL}
unzip eyesense-ai-distro.zip
cd distro
./setup.sh`}
        />
        <Typography variant="body2">
          Units that are already installed wait for <code>eyesense-gateway-update.timer</code>.
          The timer also checks the running Portainer image and, when the gateway&apos;s GPU profile
          enables it, Ollama. Keep <code>TRUSTED_ORIGINS=https://eyesense.ai</code> in compose.
        </Typography>
      </Section>
      <Alert severity="warning">
        A new zip updates installed gateways through their nightly timers. It leaves the live
        eyesense checkout and any USB stick that was already flashed on the previous zip. Publish
        the ISO after this step when new sticks should carry the same software.
      </Alert>
    </>
  );
}

function UsbIsoTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        The USB installer is the convenient, repeatable way to prepare a complete gateway computer
        from a blank internal disk. It performs an unattended Ubuntu 24.04 Desktop installation,
        installs Docker, stages the EyeSense gateway software, installs the first-boot wizard and
        nightly updater, and selects services based on the detected GPU.
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        The stick contains no gateway identity, device token, environment file, or WireGuard keys.
        Identity is created later through first-boot setup and hub claiming. The ISO embeds the
        published gateway software, so publish the zip before building the ISO.
      </Typography>
      <Section title="1. Confirm the zip publish">
        <Typography variant="body2">
          Finish the Gateway software tab first. <code>prepare-usb-installer.sh</code> downloads{' '}
          <Link href={ZIP_URL} target="_blank" rel="noopener noreferrer">
            the public zip
          </Link>{' '}
          into <code>payload/</code>. An ISO built against an older zip ships that older software.
        </Typography>
      </Section>
      <Section title="2. Remaster the ISO">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          From the <code>eyesense-usb-installer</code> folder in the llm-ocr checkout. Leave about
          22 GB free. The script reuses a cached Ubuntu ISO in <code>work/cache/</code> when one is
          already there. The default run writes an ISO and does not flash a stick.
        </Typography>
        <CommandBlock
          command={`cd eyesense-usb-installer
./scripts/prepare-usb-installer.sh`}
        />
        <Typography variant="body2">
          The ISO lands in <code>out/eyesense-usb-installer.iso</code>. Use <code>--online-only</code>
          only for a lab stick that should download the zip during install. Factory sticks embed the zip.
        </Typography>
      </Section>
      <Section title="3. Publish the ISO, the SHA, and the stick card">
        <CommandBlock
          command={`cd eyesense-usb-installer
./scripts/publish-iso.sh`}
        />
        <Typography variant="body2" sx={{ mb: 1 }}>
          The script uploads three objects and fails unless each public GET returns 200 and the
          sidecar matches:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <li>
            <Link href={ISO_URL} target="_blank" rel="noopener noreferrer">
              eyesense-usb-installer.iso
            </Link>
          </li>
          <li>eyesense-usb-installer.iso.sha256</li>
          <li>eyesense-usb-installer.txt (login and QC notes, no tokens)</li>
        </Box>
        <CommandBlock
          command={`curl -sI ${ISO_URL} | head -5
curl -fsSL ${ISO_URL}.sha256`}
        />
      </Section>
      <Section title="4. Flash a stick when you need physical media">
        <Typography variant="body2" sx={{ mb: 1 }}>
          On a Mac, run <code>diskutil list</code> and use the whole disk (<code>/dev/diskN</code>),
          the same disk that disappears when you unplug the stick. Then:
        </Typography>
        <CommandBlock
          command={`cd eyesense-usb-installer
./scripts/prepare-usb-installer.sh --flash-only --device /dev/diskN --yes`}
        />
      </Section>
      <Section title="5. If you are testing in vSphere or VMware">
        <Steps
          items={[
            'Attach the ISO as a CD/DVD. A second hard disk will not boot the installer. Prefer a SATA CD. IDE is very slow.',
            'Turn Secure Boot off.',
            'ISO-only smoke test: 4 vCPU, 6 GB RAM, 64 GB disk. A full gateway VM with no GPU: 16 GB RAM, 80 GB disk, 4 vCPU. With NVIDIA or AMD passthrough: 24 GB RAM, 128 GB disk, 8 vCPU.',
            'A crash that says “Something went wrong” with exit status 100 is usually the VM shape, not a bad download.',
            'After Ubuntu is on the disk, disconnect the ISO and boot the virtual disk. The wizard and nightly timer are already installed.',
          ]}
        />
      </Section>
      <Alert severity="info">
        Operator notes for customers who download the ISO live on{' '}
        <Link href="https://eyesense.ai/install-gateway" target="_blank" rel="noopener noreferrer">
          /install-gateway
        </Link>
        . The factory overview page is{' '}
        <Link href="https://eyesense.ai/factory/usb-installer" target="_blank" rel="noopener noreferrer">
          /factory/usb-installer
        </Link>
        .
      </Alert>
    </>
  );
}

function GoldenImageTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Use this to freeze the first factory batch as clones of one tested gateway disk. Prepare
        one Kubuntu OEM machine, remove its unit-specific identity, then capture it with Clonezilla
        or <code>dd</code>. The project plan uses this path for the first ten units; later units use
        the repeatable USB installer instead of reminting Clonezilla for every batch.
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        When a customer powers on a cloned unit, the first-boot wizard creates that unit&apos;s
        identity and guides the customer through claiming it on eyesense.ai. The customer never
        clones a repository or compiles software.
      </Typography>
      <Section title="1. Publish the zip first">
        <Typography variant="body2">
          Finish the Gateway software tab. <code>baker.sh</code> tells the OEM box to download that
          S3 zip unless you pass <code>--zip</code> with a pinned local file.
        </Typography>
      </Section>
      <Section title="2. Remint the OEM box from your laptop">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The OEM machine is already installed and logged in as <code>oem</code>. Run this from{' '}
          <code>eyesense-golden-image</code> on the laptop. The script packs the local tree, copies
          it to the OEM host, and runs the bake in tmux. Host defaults live in <code>baker.sh</code>.
        </Typography>
        <CommandBlock
          command={`cd eyesense-golden-image
./baker.sh
./baker.sh status`}
        />
        <Typography variant="body2">
          Optional image cache before you seal: <code>./baker.sh pull</code>. Logs:{' '}
          <code>./baker.sh logs</code>. Attach to the session: <code>./baker.sh attach</code>.
        </Typography>
      </Section>
      <Section title="3. Seal, then capture the disk">
        <Typography variant="body2" sx={{ mb: 1 }}>
          On the OEM account, after <code>./baker.sh verify</code> looks clean:
        </Typography>
        <CommandBlock
          command={`sudo apt-get clean
sudo journalctl --vacuum-time=1d
history -c
sudo calamares-finish-oem`}
        />
        <Typography variant="body2">
          The machine shuts down or asks you to shut down. Capture the disk before anyone completes
          the owner first-boot wizard. Clonezilla or a <code>dd</code> image both work. Record the
          build id from <code>/opt/eyesense/BUILD_ID</code> with the image.
        </Typography>
      </Section>
      <Section title="4. QC one cloned unit">
        <Steps
          items={[
            'Power on as a new owner and finish the on-screen wizard.',
            'Claim the gateway on eyesense.ai with the token the wizard shows.',
            'Confirm the hub shows the gateway and CONNECT opens the gateway UI.',
          ]}
        />
      </Section>
      <Alert severity="warning">
        Keep the bake on the scripts: copy the tree with <code>baker.sh</code>, and leave owner
        setup and <code>calamares-finish-oem</code> for after the bake verifies. A test registration
        on the OEM box must be cleared (<code>docker compose down -v</code> on that test stack)
        before you seal.
      </Alert>
    </>
  );
}

function EyesenseTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        The home eyesense server runs the live DeerAlarm gateway from a git checkout at{' '}
        <code>~/deerdeterrent</code>. That folder is the same repo as <code>deeralarm</code> on your
        laptop. This path updates that one box. Field appliances stay on the S3 zip.
      </Typography>
      <Section title="1. Make sure VPN can reach the house">
        <Typography variant="body2" sx={{ mb: 1 }}>
          From the llm-ocr checkout:
        </Typography>
        <CommandBlock command={`./scripts/check-eyesense-vpn.sh --require-ok --probe-ssh`} />
        <Typography variant="body2">
          Sony GlobalProtect on <code>gpny.sonymusic.com</code> is a full tunnel and blocks the home
          server. Disconnect it, or reconnect to <code>gp.sonymusic.com</code> (split tunnel), then
          confirm with <code>ssh eyesense &apos;echo ok&apos;</code>. A timeout or a closed connection
          on that SSH session means this VPN check still needs to pass.
        </Typography>
      </Section>
      <Section title="2. Push, then pull on the server">
        <CommandBlock
          command={`cd deeralarm
git push origin main

ssh eyesense
cd ~/deerdeterrent
git pull --ff-only
./prepare-for-docker.sh`}
        />
        <Typography variant="body2">
          Bare <code>./prepare-for-docker.sh</code> sees the WireGuard config already on this box and
          uses <code>docker-compose.gateway.yml</code>. <code>--vpn</code> forces that same file.
          Leave <code>--standalone</code> off this server. Standalone compose has no VPN and takes
          the live gateway off the hub.
        </Typography>
      </Section>
      <Section title="3. Verify">
        <CommandBlock
          command={`curl -s http://127.0.0.1:3002/api/device-info
docker compose -f docker-compose.gateway.yml ps`}
        />
        <Typography variant="body2">
          <code>hubConnected</code> should be true, and <code>registrationStatus</code> should show
          the gateway is registered. Then open the gateway from{' '}
          <Link href="https://eyesense.ai" target="_blank" rel="noopener noreferrer">
            eyesense.ai
          </Link>{' '}
          with CONNECT.
        </Typography>
      </Section>
      <Alert severity="info" sx={{ mb: 2 }}>
        Copy files onto the server with git push and git pull. A one-off scp or rsync becomes the
        next drift from origin.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        PaperIQ apps on this same machine (Patent Researcher, Agent Forge, and the other
        paperiq.ai sites) are a different stack. From that app’s own repo, commit, push, then run{' '}
        <code>./scripts/trigger-eyesense-deploy.sh</code>. Their public HTTPS path is Terraform
        CloudFront in the llm-ocr repo, plus a router port forward. That script is the wrong tool
        for <code>~/deerdeterrent</code>.
      </Typography>
    </>
  );
}

function HubImagesTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        eyesense.ai runs hub-management, hub-gateway, and hub-frontend on ECS Fargate.
        Use this tab when those images changed and the AWS resources around them did not.
        Load balancer rules, task size, and the WireGuard EC2 are the Infrastructure tab.
      </Typography>
      <Section title="Image rollout">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          From the llm-ocr <code>terraform</code> folder. The script builds linux/arm64 images, pushes
          them to ECR, and forces a new ECS deployment. The hub frontend image is built from your
          local <code>deeralarm/hub-frontend</code> tree.
        </Typography>
        <CommandBlock
          command={`cd terraform
./deploy-with-rebuild-deeralarm.sh --frontend-only
./deploy-with-rebuild-deeralarm.sh --gateway-only
./deploy-with-rebuild-deeralarm.sh --management-only
./deploy-with-rebuild-deeralarm.sh`}
        />
        <Typography variant="body2">
          Pick one scope. The last command rolls all three services. <code>--gateway-only</code> needs
          a JDK that can compile Java 21.
        </Typography>
      </Section>
      <Section title="Verify the rollout">
        <CommandBlock
          command={`aws ecs describe-services --cluster llm-ocr-cluster \\
  --services llm-ocr-hub-frontend llm-ocr-hub-gateway llm-ocr-hub-management \\
  --region us-east-1 \\
  --query 'services[].{name:serviceName,running:runningCount,desired:desiredCount,rollout:deployments[0].rolloutState}'

curl -sI https://eyesense.ai/ | head -8`}
        />
        <Typography variant="body2">
          Wait until <code>running</code> matches <code>desired</code> and <code>rollout</code> is{' '}
          <code>COMPLETED</code>. That is usually a few minutes. Log groups are{' '}
          <code>/ecs/llm-ocr-hub-frontend</code>, <code>/ecs/llm-ocr-hub-gateway</code>, and{' '}
          <code>/ecs/llm-ocr-hub-management</code>.
        </Typography>
      </Section>
    </>
  );
}

function HubInfraTab() {
  return (
    <>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Use this tab when AWS resources change: load balancer rules, task CPU or memory, environment
        in the task definition, security groups, RDS, or the WireGuard EC2. Image-only changes stay
        on the ECS images tab.
      </Typography>
      <Section title="Infrastructure change">
        <CommandBlock
          command={`aws sts get-caller-identity
cd terraform
terraform plan
terraform apply`}
        />
        <Typography variant="body2">
          Read the plan before apply. Create and edit AWS resources in Terraform. After an apply that
          changes a task definition, use the ECS images tab if you also need fresh images.
        </Typography>
      </Section>
      <Section title="Verify the rollout">
        <CommandBlock
          command={`aws ecs describe-services --cluster llm-ocr-cluster \\
  --services llm-ocr-hub-frontend llm-ocr-hub-gateway llm-ocr-hub-management \\
  --region us-east-1 \\
  --query 'services[].{name:serviceName,running:runningCount,desired:desiredCount,rollout:deployments[0].rolloutState}'

curl -sI https://eyesense.ai/ | head -8`}
        />
        <Typography variant="body2">
          Wait until <code>running</code> matches <code>desired</code> and <code>rollout</code> is{' '}
          <code>COMPLETED</code>. That is usually a few minutes. Log groups are{' '}
          <code>/ecs/llm-ocr-hub-frontend</code>, <code>/ecs/llm-ocr-hub-gateway</code>, and{' '}
          <code>/ecs/llm-ocr-hub-management</code>.
        </Typography>
      </Section>
    </>
  );
}

const GATEWAY_TAB_BODY = {
  choose: GatewayChooseTab,
  'field-zip': FieldZipTab,
  'usb-iso': UsbIsoTab,
  'golden-image': GoldenImageTab,
  eyesense: EyesenseTab,
};

const HUB_TAB_BODY = {
  choose: HubChooseTab,
  images: HubImagesTab,
  infra: HubInfraTab,
};

function areaForTab(tab) {
  if (tab === 'aws-hub' || tab === 'images' || tab === 'infra') return 'hub';
  return 'gateway';
}

function OperatorShippingGuide() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('tab');
  const requestedArea = params.get('area');
  const area = requestedArea === 'hub' || requestedArea === 'gateway'
    ? requestedArea
    : areaForTab(requestedTab);
  const bodies = area === 'hub' ? HUB_TAB_BODY : GATEWAY_TAB_BODY;
  const tabs = area === 'hub' ? HUB_TABS : GATEWAY_TABS;
  const tab = bodies[requestedTab] ? requestedTab : 'choose';
  const Body = bodies[tab];

  const selectArea = (_event, next) => {
    const updated = new URLSearchParams(params);
    updated.set('area', next);
    const nextBodies = next === 'hub' ? HUB_TAB_BODY : GATEWAY_TAB_BODY;
    if (!nextBodies[updated.get('tab')]) updated.delete('tab');
    setParams(updated, { replace: true });
  };

  const selectTab = (_event, next) => {
    const updated = new URLSearchParams(params);
    updated.set('area', area);
    if (next === 'choose') updated.delete('tab');
    else updated.set('tab', next);
    setParams(updated, { replace: true });
  };

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      <Helmet>
        <title>Shipping and rollout — EyeSense admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Shipping and rollout
        </Typography>
        <Chip label="Admins" size="small" color="primary" />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Home gateway
          </Typography>
          <Typography variant="body2">
            Builds and updates the site-local computer that runs the camera and gateway services.
            Use Gateway software on an existing OS, Installer USB stick to prepare a complete
            computer from a blank disk, or Factory disk image to clone the first OEM batch.
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Hub
          </Typography>
          <Typography variant="body2">
            Ships eyesense.ai. That is the ECS images for the hub website and hub APIs, and Terraform
            for the load balancer, task size, security groups, and the WireGuard EC2.
          </Typography>
        </Paper>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bookmark a procedure with <code>?area=</code> and <code>?tab=</code> on this page.
      </Typography>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={area}
          onChange={selectArea}
          aria-label="Home gateway or hub"
        >
          <Tab value="gateway" label="Home gateway" />
          <Tab value="hub" label="Hub" />
        </Tabs>
      </Paper>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={selectTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Packaging options"
        >
          {tabs.map((item) => (
            <Tab key={item.id} value={item.id} label={item.label} />
          ))}
        </Tabs>
      </Paper>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Body />
      </Paper>
    </Box>
  );
}

export function OperatorShippingPage() {
  const { user } = useAuth();
  if (user?.isGatewayOwner !== true) {
    return (
      <Paper sx={{ p: 3, maxWidth: 640 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Admins only
        </Typography>
        <Typography variant="body1">
          The shipping and rollout runbook is limited to hub admins.
        </Typography>
      </Paper>
    );
  }
  return <OperatorShippingGuide />;
}

export default OperatorShippingPage;
